import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { X, Folder, FileText, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { fetchImportedFiles } from '../../../slice/sharepointSlice'

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

// Browse a site's already-imported files organized by their real (possibly
// nested) SharePoint folder path — a breadcrumb-driven view (same mechanism
// as the live SharePoint folder browser) but built from what's actually been
// imported/embedded, not a live Graph call.
const ImportedFilesBrowserModal = ({ orgId, taskId, site, aispaceColor, t, onClose }) => {
  const dispatch = useDispatch()
  const { importedFilesContent, importedFilesLoading } = useSelector(state => state.sharepoint)
  const [pathStack, setPathStack] = useState(['/'])

  const currentPath = pathStack[pathStack.length - 1]

  useEffect(() => {
    dispatch(fetchImportedFiles({ orgId, taskId, siteId: site.id, path: currentPath }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath])

  const openFolder = (name) => {
    const next = currentPath === '/' ? `/${name}` : `${currentPath}/${name}`
    setPathStack(prev => [...prev, next])
  }

  const crumbs = pathStack[pathStack.length - 1]
    .split('/')
    .filter(Boolean)

  const goToCrumb = (index) => {
    // index -1 means root
    setPathStack(prev => prev.slice(0, index + 2))
  }

  const folders = importedFilesContent?.folders || []
  const files = importedFilesContent?.files || []

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-[640px] shadow-xl overflow-hidden flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5">
          <div className="min-w-0">
            <h2 className="text-[18px] font-bold text-[#111827] truncate">{t('agentSettings.filesImportedBrowserTitle', { siteName: site.site_name })}</h2>
            <div className="flex items-center gap-1 mt-1 text-[13px] text-[#6B7280] flex-wrap">
              <button onClick={() => goToCrumb(-1)} className="hover:underline hover:text-[#111827]" style={pathStack.length === 1 ? { color: aispaceColor, fontWeight: 600 } : {}}>
                {t('agentSettings.filesImportedBrowserRoot')}
              </button>
              {crumbs.map((c, i) => (
                <span key={i} className="flex items-center gap-1">
                  <ChevronRight size={12} />
                  <button onClick={() => goToCrumb(i)} className="hover:underline hover:text-[#111827]" style={i === crumbs.length - 1 ? { color: aispaceColor, fontWeight: 600 } : {}}>
                    {c}
                  </button>
                </span>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors flex-shrink-0">
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        <div className="px-6 pb-5 flex-1 overflow-y-auto">
          {pathStack.length > 1 && (
            <button
              onClick={() => setPathStack(prev => prev.slice(0, -1))}
              className="flex items-center gap-1.5 text-[13px] font-medium text-[#374151] hover:text-[#111827] mb-3"
            >
              <ChevronLeft size={16} />
              {t('agentSettings.filesImportedBrowserBack')}
            </button>
          )}

          {importedFilesLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin" style={{ color: aispaceColor }} />
            </div>
          ) : folders.length === 0 && files.length === 0 ? (
            <p className="text-[14px] text-[#6B7280] text-center py-12">{t('agentSettings.filesImportedBrowserEmpty')}</p>
          ) : (
            <div className="bg-white border border-[#EEEEEE] rounded-xl overflow-hidden">
              {folders.map((f, idx) => (
                <button
                  key={`folder-${f.name}`}
                  onClick={() => openFolder(f.name)}
                  className={`w-full flex items-center gap-3 py-3 px-4 hover:bg-[#F9FAFB] transition-colors text-left ${(idx !== folders.length - 1 || files.length > 0) ? 'border-b border-[#EEEEEE]' : ''}`}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${aispaceColor}15` }}>
                    <Folder size={18} style={{ color: aispaceColor }} />
                  </div>
                  <span className="text-[14px] font-medium text-[#111827] flex-1 truncate">{f.name}</span>
                  <span className="text-[13px] text-[#616161] flex-shrink-0">{t('agentSettings.filesImportedBrowserFileCount', { count: f.file_count })}</span>
                  <ChevronRight size={16} className="text-[#9CA3AF] flex-shrink-0" />
                </button>
              ))}
              {files.map((file, idx) => (
                <div
                  key={file.id}
                  className={`flex items-center gap-3 py-3 px-4 ${idx !== files.length - 1 ? 'border-b border-[#EEEEEE]' : ''}`}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F3F4F6' }}>
                    <FileText size={18} className="text-[#6B7280]" />
                  </div>
                  <span className="text-[14px] text-[#111827] flex-1 truncate" title={file.name}>{file.name}</span>
                  <span className="text-[13px] text-[#616161] flex-shrink-0">{formatDate(file.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ImportedFilesBrowserModal
